// ****************** NOTE ******************
// Type changes are following flow below:
// .
// .
// GET = api: TPermissionFromApi --> class: TUserAccessControlModel --> component: TAccessAndPermissionInput
// PUT = reverse it!
//
// Why three types of data? because we have three places to use:
// 1.

import { Base, FetchingServices } from '@/composables/base';
import { reactive } from 'vue';
import { useSettingsRAP } from '.';
import {
  AccessEntries,
  type TAccess,
  type TAccessGroup,
  type TCustomPermission,
  type TLinks,
  type TNoPolicyPermission,
  type TPermission,
  type TPolicy,
} from './AccessEntries';

//  |
//  |   Role Types
// @@@
//  @

type TPermissionFromApi = {
  permissions: {
    [accessKey in TAccess]: {
      [permKey in TNoPolicyPermission]: boolean;
    } & {
      [permKey in Exclude<
        TPermission | TCustomPermission,
        TNoPolicyPermission
      >]: Partial<Record<TPolicy, boolean>>;
    };
  };
  description: string;
  role: string;
};
export type TRecordAccessApi = {
  [key in TPermission | TCustomPermission]: boolean;
};

export type TRoleModel = {
  default: boolean;
  role: string;
  description: string;
};

export type TUserAccessControlModel = {
  role: string;
  description: string;
  list: {
    access: TAccess;
    permission: TPermission | TCustomPermission;
    policy: TPolicy | null;
  }[];
};

export type TAccessControlList = {};
export type TInfoFormData = { role: string; description: string };

//  |
//  |   Filter Criterias type
// @@@
//  @
type TFilterCriterias = {
  page_number: number;
  page_size: number;
};

//  |
//  |   Component type
// @@@
//  @
export type TAccessAndPermissionInput = {
  role: string;
  name: string;
  key: TAccessGroup;
  access: {
    name: string;
    key: TAccess;
    accessPanelIsOpen: number | null;
    hasOwnerPaid: boolean;
    selectAll: string | boolean;
    permissions: {
      name: string;
      key: TPermission | TCustomPermission;
      isChecked: TPermission | TCustomPermission | false;
      policy: TPolicy | null;
    }[];
  }[];
};

export type TPolicyPriorityInput = {
  title: string;
  value: TPolicy;
  priority: number;
  props:
    | {
        disabled: boolean;
        baseColor: string;
      }
    | undefined;
};

//  |
//  |   Interface and Class
// @@@
//  @
interface IRolesAndPermissions {
  roleList: TRoleModel[];
  selectedRole: TUserAccessControlModel | null;
  isRoleFetching: boolean;

  // filterCriterias: TFilterCriterias;

  // private
  // updateSidebarMenuVisibility(): void;
  // generateUpsertBody(formData: TUserAccessControlModel): TPermissionFromApi

  // api
  apiGetRoleList(): Promise<void>;
  apiGetRole(role: string): Promise<TPermissionFromApi | null>;
  apiUpsertRole(formData: TUserAccessControlModel): Promise<boolean>;
  apiDeleteRole(role: string): Promise<boolean>;

  // setters & getters
  setRoleList(list: TRoleModel[]): void;
  getRoleList(): TRoleModel[];

  // public
  clearSelectedRole(): void;

  // norms
  normalizeUserPermissionApiToClassType(
    data: TPermissionFromApi
  ): TUserAccessControlModel;
  normalizePermissionFormDataToClassType(
    infoFormData: TInfoFormData,
    formData: TAccessAndPermissionInput[]
  ): TUserAccessControlModel;
}
export class RoleService extends Base implements IRolesAndPermissions {
  roleList: TRoleModel[] = [];
  selectedRole: TUserAccessControlModel | null = null;
  isRoleFetching: boolean = false;

  // private
  private generateUpsertBody(
    formData: TUserAccessControlModel
  ): TPermissionFromApi {
    const permissions: Partial<TPermissionFromApi['permissions']> = {};

    for (const item of formData.list) {
      const { access, permission, policy } = item;

      // Initialize access group if needed
      if (!permissions[access]) {
        permissions[access] = {} as Record<
          TPermission | TCustomPermission,
          any // we'll handle type dynamically
        >;
      }

      // Special case: "add" permission
      if (isNoPolicyPermission(permission)) {
        permissions[access][permission] = true;
      }
      // else if (SPECIFIC_NO_POLICY_PERMISSION[access]?.includes(permission)) {
      //   permissions[access][permission as TNoPolicyPermission] = true;
      // }
      else {
        // Normal permissions
        if (!policy) continue; // skip if no policy selected

        permissions[access][permission] = {
          [policy]: true,
        };
      }
    }

    return {
      role: formData.role,
      description: formData.description,
      permissions: permissions as TPermissionFromApi['permissions'],
    };
  }
  private filterRoleBasedOnVisibility(
    userRole: TUserAccessControlModel
  ): TUserAccessControlModel {
    if (!useSettingsRAP.visibilityList) {
      console.error('visibility list not found');
      return { ...userRole, list: [] };
    }
    // remove roles if there is not in visibility
    const filteredRole: TUserAccessControlModel = { ...userRole, list: [] };
    const accessEntry = [...AccessEntries.entries()];
    userRole.list.forEach((item) => {
      const userAccessTuple = accessEntry.find((key) => {
        return key[1].user.includes(item.access);
      });

      if (userAccessTuple) {
        userAccessTuple[1].owner.forEach((ownerAccess) => {
          if (
            useSettingsRAP.visibilityList &&
            useSettingsRAP.visibilityList[ownerAccess]
          ) {
            filteredRole.list.push(item);
          }
        });
      }
    });
    return filteredRole;
  }

  async apiGetRoleList() {
    try {
      const res = await this.get<TRoleModel[]>(
        FetchingServices.ipbx,
        'permissionsV2/all'
      );

      if (res.data) {
        // Normalize the data: bring the default roles to first index
        const defaultRoles = res.data.filter((role) => role.default === true);
        const otherRoles = res.data.filter((role) => role.default === false);
        this.setRoleList([...defaultRoles, ...otherRoles]);
      }
    } catch (err) {
      // handel error
      console.log('get role list error');
    }
  }
  async apiGetRole(
    role: string,
    noSaveToState?: boolean
  ): Promise<TPermissionFromApi | null> {
    this.isRoleFetching = true;
    let returnVal: TPermissionFromApi | null = null;

    try {
      const res = await this.get<TPermissionFromApi>(
        FetchingServices.ipbx,
        `permissionsV2/${role}`
      );
      if (res.data) {
        if (!noSaveToState) {
          const convertedData = this.normalizeUserPermissionApiToClassType(
            res.data
          );
          const filteredData = this.filterRoleBasedOnVisibility(convertedData);
          this.setSelectedRole(filteredData);
        }
        returnVal = res.data;
      }
    } finally {
      this.isRoleFetching = false;
      return returnVal;
    }
  }
  async apiUpsertRole(formData: TUserAccessControlModel): Promise<boolean> {
    const generatedBody = this.generateUpsertBody(formData);
    const body: TPermissionFromApi = generatedBody;
    let returnVal: boolean = false;
    try {
      const res = await this.post<string>(
        FetchingServices.ipbx,
        'permissionsV2/upsert',
        {
          ...body,
        }
      );
      if (res.status === 200) returnVal = true;
    } finally {
      return returnVal;
    }
  }
  async apiDeleteRole(role: string): Promise<boolean> {
    try {
      const res = await this.delete<boolean>(
        FetchingServices.ipbx,
        `permissionsV2/${role}`
      );
      if (res.data) return true;
      return false;
    } catch (err) {
      console.log('delete role error');
      return false;
    }
  }

  // Setters
  setRoleList(list: TRoleModel[]): void {
    this.roleList = [...list];
  }
  setSelectedRole(role: TUserAccessControlModel): void {
    this.selectedRole = { ...role };
  }

  // Getters
  getRoleList(): TRoleModel[] {
    return [...this.roleList];
  }

  // Publics
  public clearSelectedRole() {
    this.selectedRole = null;
  }

  // norms
  public normalizeUserPermissionApiToClassType(
    data: TPermissionFromApi
  ): TUserAccessControlModel {
    const convertedData: TUserAccessControlModel = {
      role: data.role,
      description: data.description,
      list: [],
    };

    // Normalize data
    for (const access of Object.keys(data.permissions) as TAccess[]) {
      for (const permission of Object.keys(data.permissions[access]) as (
        | TPermission
        | TCustomPermission
      )[]) {
        // Manage if the permission is NO_POLICY_PERMISSION_LIST
        if (
          typeof data.permissions[access][permission] === 'boolean' &&
          data.permissions[access][permission]
        ) {
          convertedData.list.push({
            access,
            permission,
            policy: null,
          });
        }
        // NOTE: if permission is not NO_POLICY_PERMISSION_LIST it has an object of Policies
        else
          for (const policy of Object.keys(
            data.permissions[access][permission]
          ) as TPolicy[]) {
            // NOTE: I ignore the type because it thinks it
            // can be a boolean but i have checked it before so
            // it is not
            // @ts-ignore
            if (data.permissions[access][permission][policy]) {
              // this access has
              convertedData.list.push({
                access,
                permission,
                policy,
              });
            }
          }
      }
    }

    return convertedData;
  }
  public normalizePermissionFormDataToClassType(
    infoFormData: TInfoFormData,
    formData: TAccessAndPermissionInput[]
  ): TUserAccessControlModel {
    return {
      // pick a role — adjust depending on your actual logic
      role: infoFormData.role, // or extract from another source
      description: infoFormData.description,

      // flatten all access and permission entries
      list: formData.flatMap((group) =>
        group.access.flatMap((accessItem) =>
          accessItem.permissions
            .filter((p) => p.isChecked !== false)
            .map((p) => ({
              access: accessItem.key,
              permission: (p.isChecked || p.key) as
                | TPermission
                | TCustomPermission,
              policy: p.policy ?? null,
            }))
        )
      ),
    };
  }
}

export const useSettingsRAPRoleService = reactive(new RoleService());

// Authenticated User
// user
// |__role
// |__access
//    |__permission
//       |__policy

// All Accesses
export const ALL_GROUP_ACCESSES: { [key in TAccessGroup]: TAccess[] } = {
  crm: [
    'timeline',
    'ci_persons',
    'ci_companies',
    'deal',
    'lead',
    'ticket',
    'task',
    'invoice',
    'invoice_settings',
    'product',
    'inventory',
  ],
  reportsRAP: [
    'call_report_missed',
    'call_logs',
    'call_report_unanswerd',
    'operators_activity',
    'call_records',
    'full_call_records',
    'call_scattering',
    'rating',
    'cost_log',
    'customer_waiting',
  ],
  wallboard: ['wallboard', 'live_monitoring'],
  sms: [
    'sms_contacts',
    'sms_single_send',
    'sms_group_send',
    'sms_bulk_send',
    'sms_p2p_send',
    'sms_parametric_send',
    'sms_developers_section',
    'sms_messages_detail',
    'sms_inbounds',
  ],
  webchat: [
    'webchat_counter',
    'webchat_declare_website',
    'webchat_department',
    'webchat_operator_management',
    'webchat_personalization',
    'webchat_chats',
  ],
  qaqc: ['evaluations', 'evaluation_report'],
  fax: ['fax_create', 'fax_send', 'fax_sent_report', 'fax_recv_report'],
  videoConference: ['conf_create_link', 'conf_report'],
  userSettings: [
    'role_management',
    'department',
    'permission_management',
    'whatsapp',
    'message_to_operator',
    'stream_record_sound',
  ],
  callSettings: [
    'call_forward',
    'upload_voice',
    'queue',
    'ivr_menu',
    'time_cond',
    'voicemail_management',
    'block_list',
    'destinations',
  ],
  connectToCrm: ['crm_authorized_numbers', 'crm_settings', 'crm_links'],
  automation: ['workflow'],
  // settings: [],
};

// baseline permissions for everyone
const BASE_PERMISSIONS: TPermission[] = ['view', 'add', 'edit', 'delete'];

// per-access custom extras
const CUSTOM_PERMISSIONS: Partial<Record<TAccess, TCustomPermission[]>> = {
  ci_companies: ['report'],
  ci_persons: ['report'],
  lead: ['assign', 'convert', 'report'],
  product: [
    'create_category',
    'delete_category',
    'edit_category',
    'report',
    'upload',
  ],
  message_send_report: ['report'],
  ticket: ['answer', 'assign', 'change_status', 'close', 'title_options'],
  deal: ['assign', 'change_status', 'report', 'title_options'],
  invoice: ['add_pre_invoice', 'convert', 'invoke', 'report', 'upload'],
  inventory: ['add_product', 'report'],
  sms_contacts: ['delete_group', 'view_group', 'edit_group', 'add_group'],
  sms_inbounds: ['report'],
  sms_messages_detail: ['report'],
  webchat_operator_management: ['delete_agent'],
  webchat_declare_website: [],
  task: ['change_status', 'comment', 'report', 'title_options'],
  call_logs: ['report'],
  operators_activity: ['report'],
  call_records: ['report'],
  full_call_records: ['report'],
  rating: ['report'],
};

export const POLICIES: { key: TPolicy; priority: number }[] = [
  { key: 'self', priority: 0 },
  { key: 'self_and_role_mates', priority: 1 },
  { key: 'self_and_department_mates', priority: 2 },
  { key: 'all', priority: 3 },
];

// Global NO Policy Permissions
export const NO_POLICY_PERMISSION_LIST: (TPermission | TCustomPermission)[] = [
  'add',
  'report',
  'delete_agent',
  'create_category',
  'delete_category',
  'edit_category',
  'add_product',
  'upload',
  'add_pre_invoice',
  'delete_group',
  'add_group',
  'view_group',
  'edit_group',
  'title_options',
];
export const isNoPolicyPermission = (
  p: TPermission | TCustomPermission
): p is TNoPolicyPermission => NO_POLICY_PERMISSION_LIST.includes(p);

// Specific No Policy Permission
// export const SPECIFIC_NO_POLICY_PERMISSION: Partial<
//   Record<TAccess, (TPermission | TCustomPermission)[]>
// > = {
//   webchat_personalization: ['view', 'edit'],
// };

// Exception Permissions in access
export const EXECPTION_ACCESS_WITH_LESS_PERMISSIONS: Partial<
  Record<TAccess, (TPermission | TCustomPermission)[]>
> = {
  timeline: ['add', 'edit', 'delete'],

  call_logs: ['add', 'edit', 'delete'],

  permission_management: ['edit'],
  message_to_operator: ['add', 'edit', 'delete'],
  whatsapp: ['edit', 'delete'],

  fax_create: ['add', 'edit', 'delete'],
  fax_send: ['add', 'edit', 'delete'],
  fax_sent_report: ['add', 'edit'],
  fax_recv_report: ['add', 'edit', 'delete'],

  webchat_counter: ['add', 'edit', 'delete'],
  webchat_declare_website: ['delete'],
  webchat_personalization: ['add', 'delete'],
  webchat_operator_management: ['add', 'edit', 'delete'],
  webchat_chats: ['add', 'delete', 'edit'],

  invoice_settings: ['add', 'delete'],

  // SMS
  sms_single_send: ['add', 'delete', 'edit'],
  sms_group_send: ['add', 'delete', 'edit'],
  sms_bulk_send: ['add', 'delete', 'edit'],
  sms_p2p_send: ['add', 'delete', 'edit'],
  sms_parametric_send: ['add', 'delete', 'edit'],
  sms_developers_section: ['add', 'delete', 'edit'],
  sms_messages_detail: ['add', 'delete', 'edit'],
  sms_inbounds: ['add', 'delete', 'edit'],

  call_report_unanswerd: ['add', 'delete', 'edit'],
  operators_activity: ['add', 'delete', 'edit'],
  call_records: ['add', 'edit'],
  full_call_records: ['add', 'delete', 'edit'],
  call_scattering: ['add', 'delete', 'edit'],
  rating: ['add', 'delete', 'edit'],
  cost_log: ['add', 'delete', 'edit'],
  customer_waiting: ['add', 'delete', 'edit'],
  call_report_missed: ['add', 'delete', 'edit'],
  stream_record_sound: ['add', 'delete', 'edit'],

  //automation
  workflow: ['add', 'delete', 'edit'],
};

// Custom Forced Policy
export const CUSTOM_FORCED_POLICY: Partial<
  Record<TAccess, Partial<Record<TPermission | TCustomPermission, TPolicy>>>
> = {
  timeline: {
    view: 'all',
  },

  role_management: {
    view: 'all',
    edit: 'all',
    delete: 'all',
  },

  fax_create: {
    view: 'all',
  },
  fax_send: {
    view: 'all',
  },
  fax_sent_report: {
    view: 'all',
    delete: 'all',
  },
  fax_recv_report: {
    view: 'all',
  },

  department: {
    view: 'all',
    edit: 'all',
    delete: 'all',
  },
  permission_management: {
    view: 'all',
    edit: 'all',
    delete: 'all',
  },
  message_to_operator: {
    view: 'all',
  },
  whatsapp: {
    view: 'all',
  },

  webchat_operator_management: {
    view: 'all',
  },
  webchat_personalization: {
    view: 'all',
    edit: 'all',
  },
  invoice_settings: {
    view: 'all',
    edit: 'all',
  },

  // SMS
  sms_contacts: {
    view: 'all',
    edit: 'all',
    delete: 'all',
  },
  sms_single_send: { view: 'all' },
  sms_group_send: { view: 'all' },
  sms_bulk_send: { view: 'all' },
  sms_p2p_send: { view: 'all' },
  sms_parametric_send: { view: 'all' },
  sms_developers_section: { view: 'all' },
  sms_inbounds: { view: 'all' },

  call_logs: { view: 'all' },
  call_report_unanswerd: { view: 'all' },
  operators_activity: { view: 'all' },
  call_records: { view: 'all', delete: 'all' },
  full_call_records: { view: 'all' },
  call_scattering: { view: 'all' },
  rating: { view: 'all' },
  cost_log: { view: 'all' },
  customer_waiting: { view: 'all' },
  call_report_missed: { view: 'all' },
  stream_record_sound: { view: 'all' },

  //automation
  workflow: { view: 'all' },
};

// Note: this works with CUSTOM_FORCED_POLICY. when you hide a policy it means you have to set
// at least one policy and force it to any policy service needs.
export const EXCEPTION_BASE_PERMISSION_WITH_HIDDEN_POLICY: Partial<
  Record<TAccess, Partial<Array<TPermission | TCustomPermission>>>
> = {
  //CRM
  timeline: ['view'],

  // USER SETTINGS
  invoice_settings: ['view', 'edit'],
  fax_create: ['view'],
  fax_send: ['view'],
  fax_sent_report: ['view', 'delete'],
  fax_recv_report: ['view'],
  role_management: ['view', 'edit', 'delete'],
  department: ['view', 'edit', 'delete'],
  permission_management: ['view', 'edit', 'delete'],
  message_to_operator: ['view'],
  whatsapp: ['view'],

  // SMS
  sms_contacts: ['edit', 'delete', 'view'],
  sms_single_send: ['view'],
  sms_group_send: ['view'],
  sms_bulk_send: ['view'],
  sms_p2p_send: ['view'],
  sms_parametric_send: ['view'],
  sms_developers_section: ['view'],
  sms_inbounds: ['view'],

  // REPORTS
  call_logs: ['view'],
  call_report_unanswerd: ['view'],
  operators_activity: ['view'],
  call_records: ['view'],
  full_call_records: ['view'],
  call_scattering: ['view'],
  rating: ['view'],
  cost_log: ['view'],
  customer_waiting: ['view'],
  call_report_missed: ['view'],
  stream_record_sound: ['view'],

  //automation
  workflow: ['view'],
};

// Custom Permission Name For Localization Based On Access
export const CUSTOM_PERMISSION_NAME_FOR_LOCALIZATION: Partial<
  Record<TAccess, Partial<Record<TPermission | TCustomPermission, string>>>
> = {
  invoice: {
    convert: 'convert_to_invoice',
    add: 'add_invoice',
  },
  permission_management: {
    add: 'add_and_edit',
  },
  whatsapp: {
    view: 'messaging_in_whatsapp',
    add: 'whatsapp_settings',
  },
  stream_record_sound: {
    view: 'stream_record_sound_view',
  },
};

// List of Conditional Permissions NOTE: not implemented yet
export const CONDITIONAL_PERMISSIONS_LIST: Partial<
  Record<
    TAccess,
    Partial<
      Record<
        TPermission | TCustomPermission,
        Partial<
          Record<
            TAccess,
            Partial<Record<TPermission | TCustomPermission, TPolicy | null>>
          >
        >
      >
    >
  >
> = {
  product: {
    delete_category: {
      product: {
        view: 'all',
        edit: 'all',
        delete: 'all',
      },
    },
    edit_category: {
      product: {
        view: 'all',
        edit: 'all',
        delete: 'all',
      },
    },
  },
};

// product: {
//   delete_category: {
//     product: {
//       view: 'all';
//     }
//   }
// }

// Exception Permissions in permission
export const EXCEPTION_SUBMENU_BASED_ON_PERMISSION: Partial<
  Record<
    TAccess,
    Array<{ link: TLinks; permissions: Array<TPermission | TCustomPermission> }>
  >
> = {
  whatsapp: [
    {
      link: '/users/whatsapp',
      permissions: ['add'],
    },
  ],
};

// Will Implemented In Next Phases
export const UNPHASED_ACCESSES: TAccess[] = [
  'add_user',
  'block_list',
  'call',
  'call_forward',
  'conf_create_link',
  'conf_report',
  'create_tickets',
  'crm_authorized_numbers',
  'crm_links',
  'crm_settings',
  'destinations',
  'evaluation_report',
  'evaluations',
  'ivr_menu',
  'live_monitoring',
  'observe_sent_tickets',
  'queue',
  'time_cond',
  'upload_voice',
  'voicemail_management',
  'wallboard',
  'webchat_declare_website',
  'webchat_operator_management',
  'webchat_department',
  'webchat_personalization',
  'webchat_chats',
  'webchat_counter',
];

// helper to get all permissions for an access
export function extractAccessPermissions(
  access: TAccess
): (TPermission | TCustomPermission)[] {
  return [...BASE_PERMISSIONS, ...(CUSTOM_PERMISSIONS[access] ?? [])];
}
